import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useRecoilState } from 'recoil';
import { MINUTE } from 'time-constants';
import { array, number, object, string } from 'yup';

import { apiConfig } from '@constants';
import { hasResetRefetchIntervalState, hourlyForecastState } from '@store';

import { refetchIntervalByEndOfHour } from '../../helpers/refetchIntervalUtils';
import { toQueryString } from '../../helpers/StringUtils';
import { useGetUserLocation } from '../useGetUserLocation';

/**
 * Hourly Forecast endpoint returns either 2 day or 15 day weather
 * If an invalid day is passed in, return an error
 */
const hourlyForecastArgumentsSchema = object({
  lat: string().required(),
  lng: string().required(),
  language: string().default('en-US'),
});

const hourlyForecastSchema = object({
  cloudCover: array(number().nullable()),
  dayOfWeek: array(string().nullable()),
  dayOrNight: array(string().nullable()),
  expirationTimeUtc: array(number().nullable()),
  iconCode: array(number().nullable()),
  iconCodeExtend: array(number().nullable()),
  precipChance: array(number().nullable()),
  precipType: array(string().nullable()),
  pressureMeanSeaLevel: array(number().nullable()),
  qpf: array(number().nullable()),
  qpfSnow: array(number().nullable()),
  relativeHumidity: array(number().nullable()),
  temperature: array(number().nullable()),
  temperatureDewPoint: array(number().nullable()),
  temperatureFeelsLike: array(number().nullable()),
  temperatureHeatIndex: array(number().nullable()),
  temperatureWindChill: array(number().nullable()),
  uvDescription: array(string().nullable()),
  uvIndex: array(number().nullable()),
  validTimeLocal: array(string().nullable()),
  validTimeUtc: array(number().nullable()),
  visibility: array(number().nullable()),
  windDirection: array(number().nullable()),
  windDirectionCardinal: array(string().nullable()),
  windGust: array(number().nullable()),
  windSpeed: array(number().nullable()),
  wxPhraseLong: array(string().nullable()),
  wxPhraseShort: array(string().nullable()),
  wxSeverity: array(number().nullable()),
});

const getApiUrl = ({ lat, lng, language }) =>
  [
    apiConfig.TWC_API_BASE,
    apiConfig.TWC_API_HOURLY_FORECAST,
    '/15day',
    toQueryString({
      language,
      apiKey: apiConfig.TWC_API_KEY,
      geocode: `${lat},${lng}`,
      units: 'e',
      format: 'json',
    }),
  ].join('');

const getHourlyForecast = async parameters => {
  const validateArguments = await hourlyForecastArgumentsSchema.validate(parameters);

  const url = getApiUrl(validateArguments);
  const { data } = await axios.get(url);

  return hourlyForecastSchema.validate(data);
};

export const useGetHourlyForecast = () => {
  const {
    coords: { lat, lng },
  } = useGetUserLocation();
  const [state, setState] = useRecoilState(hourlyForecastState);
  const [refetchInterval, setRefetchInterval] = useState(10 * MINUTE);

  const [hasResetRefetchInterval, setHasResetRefetchInterval] = useRecoilState(hasResetRefetchIntervalState);
  const { hourlyWeather: hourlyWeatherRefetched } = hasResetRefetchInterval;

  // Trigger refetch when Debug date is set
  useEffect(() => {
    if (Date.prototype.debugDate && !hourlyWeatherRefetched) {
      console.log('Refetching Hourly Weather');
      refetchIntervalByEndOfHour({ setRefetchInterval });
      setHasResetRefetchInterval({
        ...hasResetRefetchInterval,
        hourlyWeather: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Date.prototype.debugDate]);

  const { data, error, isFetched, isLoading } = useQuery(
    ['getHourlyForecast', lat, lng],
    () => getHourlyForecast({ lat, lng }),
    {
      enabled: Boolean(lat && lng),
      refetchOnWindowFocus: false,
      refetchInterval,
      retry: false,
      onSettled: _data => {
        if (_data) {
          refetchIntervalByEndOfHour({ setRefetchInterval });
        } else {
          setRefetchInterval(2 * MINUTE);
        }
      },
    },
  );

  useEffect(() => {
    setState({ data, error, isFetched, isLoading });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading, isFetched]);

  return state;
};
